import {
  Directive,
  ElementRef,
  HostListener,
  InjectionToken,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  SecurityContext,
} from '@angular/core';
import { NgControl, ValidationErrors } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FormErrorMessage } from './form-error-message';

export interface FormError {
  key: string;
  value: string;
}

export const FORM_ERROR_MESSAGES = new InjectionToken<FormErrorMessage>(
  'FORM_ERROR_MESSAGES'
);

@Directive({
  standalone: false,
  selector: '[appFormErrorMessage]',
})
export class FormErrorMessageDirective implements OnInit, OnDestroy {
  @Input() id: string;

  @Input() customFormErrorMessages: FormError[];

  statusChangeSubscription: Subscription;

  private errorSpanId = '';
  private formErrorMessages: FormErrorMessage;
  private destroy$ = new Subject<void>();
  private errorSpanElement: HTMLElement | null = null;

  constructor(
    private renderer: Renderer2,
    private translateService: TranslateService,
    private elRef: ElementRef,
    private control: NgControl,
    private injector: Injector,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.formErrorMessages = this.injector.get(FORM_ERROR_MESSAGES);
    this.errorSpanId = this.id + '-error';

    this.statusChangeSubscription = this.control.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        if (status === 'INVALID') {
          this.showError();
        } else {
          this.removeError();
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('blur', ['$event'])
  handleBlurEvent(event) {
    if (this.control.errors) {
      this.showError();
    } else {
      this.removeError();
    }
  }

  private showError() {
    this.removeError();

    const valErrors: ValidationErrors = this.control.errors;
    const errorKey = Object.keys(valErrors)[0];

    let formErrorMessage;
    if (this.customFormErrorMessages) {
      formErrorMessage = this.customFormErrorMessages.find(
        (x) => x.key === errorKey
      );
    }
    if (!formErrorMessage) {
      formErrorMessage = this.formErrorMessages
        .getMessages()
        .find((x) => x.key === errorKey);
    }

    if (formErrorMessage) {
      const errorMsg = this.translateService.instant(
        formErrorMessage.value,
        valErrors
      );

      // ✅ Sanitizar a mensagem de erro para prevenir XSS
      const sanitizedErrorMsg =
        this.sanitizer.sanitize(SecurityContext.HTML, errorMsg) || '';

      // ✅ Criar elemento de erro
      const errorSpan = this.renderer.createElement('span');
      this.renderer.addClass(errorSpan, 'form__invalid-field-message');
      this.renderer.setAttribute(errorSpan, 'id', this.errorSpanId);

      // ✅ Criar text node com conteúdo sanitizado
      const textNode = this.renderer.createText(sanitizedErrorMsg);

      // ✅ Adicionar text node ao span (sanitizado, então seguro)
      this.renderer.appendChild(errorSpan, textNode);

      // ✅ Adicionar span ao DOM usando Renderer2
      this.renderer.appendChild(
        this.elRef.nativeElement.parentElement,
        errorSpan
      );
      this.renderer.addClass(this.elRef.nativeElement, 'form__invalid-field');

      // Armazenar referência
      this.errorSpanElement = errorSpan;
    }
  }

  private removeError(): void {
    if (this.errorSpanElement) {
      this.renderer.removeClass(
        this.elRef.nativeElement,
        'form__invalid-field'
      );
      this.renderer.removeChild(
        this.elRef.nativeElement.parentElement,
        this.errorSpanElement
      );
      this.errorSpanElement = null;
    }
  }
}
